import React, { useState, useEffect } from 'react';
import { withFormik } from 'formik';
import * as Yup from 'yup';
// components
import Modal from 'shared/modals/Modal';
import LoadingModal from 'shared/loading/LoadingModal';
import toastr from 'shared/helpers/toastr';
import errorToastr from 'shared/helpers/errorToastr';
// fetch
import { getOrderTracing, executeTrace } from 'src/fetch/orders';
import { getTraceLocations } from 'src/fetch/tracing';
// helpers
import { dateFormatter, longDateTimeFormatter } from 'shared/../constants/Formatting';
import BaseSelect from 'src/shared/components/inputs/BaseSelect';
import BaseInput from 'src/shared/components/inputs/BaseInput';
import { Button } from '@progress/kendo-react-buttons';

import { useSelector } from 'react-redux';
import { getMoveTypes } from 'src/slices/moveTypes';

const TracingModal = ({
  values,
  initialValues,
  isOpen,
  close,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  touched,
  errors,
  isSubmitting,
  proNumber,
  equipmentNumber,
  bkgnumber,
  moveType,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [newDate, setNewDate] = useState(null);
  const [traceLocation, setTraceLocation] = useState(null);
  const [traceResults, setTraceResults] = useState([]);

  const parseLocations = async (locations) => {
    let tmp = [];
    // get Order Tracing
    tmp.push({
      group: 'Order Tracing',
      name: locations.orderShipline,
    });

    tmp.push({
      group: 'Order Tracing',
      name: locations.orderTerminal,
    });
    // get Terminals
    locations.portLocations.map((loc) => {
      tmp.push({
        group: 'Terminals',
        name: loc,
      });
    });
    // get Ports
    locations.railLocations.map((loc) => {
      tmp.push({
        group: 'Rails',
        name: loc,
      });
    });
    // get SCACs
    locations.scacLocations.map((loc) => {
      tmp.push({
        group: 'SCACs',
        name: loc,
      });
    });

    return tmp.filter((loc) => loc.name); // don't return null names
  };

  const moveTypes = useSelector(getMoveTypes);

  const load = async () => {
    setIsLoading(true);
    try {
      const rlocations = await getTraceLocations(proNumber);
      const response = await getOrderTracing(proNumber);
      const locations = await parseLocations(rlocations);
      setData(response);
      setLocations(locations);
      setIsLoading(false);
    } catch (loadError) {
      console.log('error', loadError);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChangeLocation = (event) => {
    setTraceLocation(event.target.value.name);
  };

  const handleExecuteTrace = async () => {
    setIsLoading(true);
    try {
      const response = await executeTrace({
        proNumber: proNumber,
        carrier: traceLocation,
        equipmentNumber: [equipmentNumber],
        bkgnumber: [bkgnumber],
        moveType: moveTypes.find((type) => type.value === moveType).text,
      });

      setTraceResults(response.traces);

      const date = new Date().toISOString();
      setNewDate(longDateTimeFormatter(date));
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} handleClose={() => close()} containerStyle='large'>
      <div className='modal-header'>
        <h5 className='modal-title' id='carrierModal'>
          Tracing Information
        </h5>
        <button className='button large close' onClick={() => close()} />
      </div>
      <div className='modal-body-content'>
        <LoadingModal isOpen={isLoading} isNested={true} />
        {data && (
          <>
            {' '}
            <BaseSelect
              data={locations || []}
              textField={'name'}
              groupField={'group'}
              filterable={false}
              isCombo={true}
              style={{ width: '20rem', marginTop: '1rem', marginRight: '2rem' }}
              onChange={onChangeLocation}
              checked={traceLocation}
            />
            <Button onClick={handleExecuteTrace}>Trace</Button>{' '}
          </>
        )}
        <br />
        <fieldset>
          <div>
            <label htmlFor='equipnumber' style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}>
              Equipment #
            </label>
            <BaseInput
              id='equipnumber'
              disabled={false}
              value={traceResults[0]?.equipId || ''}
              style={{ width: '20rem' }}
            />
          </div>
          <div>
            <label htmlFor='portoforigin' style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}>
              Port of Origin
            </label>
            <BaseInput
              id='portoforigin'
              disabled={false}
              value={traceResults[0]?.portOfOrigin || ''}
              style={{ width: '20rem' }}
            />
            <label
              htmlFor='portofdischarge'
              style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}
            >
              Port of Discharge
            </label>
            <BaseInput
              id='portofdischarge'
              disabled={false}
              value={traceResults[0]?.portOfDischarge || ''}
              style={{ width: '20rem' }}
            />
          </div>

          <div>
            <label htmlFor='vessel' style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}>
              Vessel
            </label>
            <BaseInput id='vessel' disabled={false} value={traceResults[0]?.vessel || ''} style={{ width: '20rem' }} />
            <label htmlFor='voyage' style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}>
              Voyage
            </label>
            <BaseInput id='voyage' disabled={false} value={traceResults[0]?.voyage || ''} style={{ width: '20rem' }} />
          </div>

          <div>
            <label htmlFor='sailestimate' style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}>
              Sail Estimate
            </label>
            <BaseInput
              id='sailestimate'
              disabled={false}
              value={dateFormatter(traceResults[0]?.vesselSailDateEst) || ''}
              style={{ width: '20rem' }}
            />
            <label htmlFor='sailactual' style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}>
              Sail Actual
            </label>
            <BaseInput
              id='sailactual'
              disabled={false}
              value={dateFormatter(traceResults[0]?.vesselSailDateTime) || ''}
              style={{ width: '20rem' }}
            />
          </div>

          <div>
            <label
              htmlFor='arrivalestimate'
              style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}
            >
              Arrival Estimate
            </label>
            <BaseInput
              id='arrivalestimate'
              disabled={false}
              value={dateFormatter(traceResults[0]?.vesselArrivalDateEst) || ''}
              style={{ width: '20rem' }}
            />
            <label htmlFor='arrivalactual' style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}>
              Arrival Actual
            </label>
            <BaseInput
              id='arrivalactual'
              disabled={false}
              value={longDateTimeFormatter(traceResults[0]?.vesselArrivalDateTime) || ''}
              style={{ width: '20rem' }}
            />
          </div>
          <div>
            <label htmlFor='eta' style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}>
              ETA
            </label>
            <BaseInput
              id='eta'
              disabled={false}
              value={dateFormatter(traceResults[0]?.eta) || ''}
              style={{ width: '20rem' }}
            />
            <label htmlFor='lfd' style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}>
              LFD
            </label>
            <BaseInput
              id='lfd'
              disabled={false}
              value={dateFormatter(traceResults[0]?.lfd) || ''}
              style={{ width: '20rem' }}
            />
          </div>
          <div>
            <label htmlFor='available' style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}>
              Available
            </label>
            <BaseInput
              id='available'
              disabled={false}
              value={longDateTimeFormatter(traceResults[0]?.availableDate) || ''}
              style={{ width: '20rem' }}
            />
            <label htmlFor='outgated' style={{ margin: '0 2rem 0 2rem', width: '6rem', display: 'inline-block' }}>
              Outgated
            </label>
            <BaseInput
              id='outgated'
              disabled={false}
              value={longDateTimeFormatter(traceResults[0]?.outgateDate) || ''}
              style={{ width: '20rem' }}
            />
          </div>
        </fieldset>
        <div className='toolbar--column toolbar--right'>
          <button
            type='button'
            className='button large white text-align--center margin--none--xxs'
            onClick={() => close()}
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

const TracingSchema = Yup.object().shape({
  name: Yup.string().required('Name is Required'),
});

export default withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues = {} }) => {
    return {
      ...initialValues,
    };
  },
  validationSchema: TracingSchema,
  handleSubmit: async (values, { setSubmitting, resetForm, props: { close } }) => {
    setSubmitting(true);

    try {
      close(true);
      resetForm();

      toastr('success', 'Tracing Notes Saved Successfully.');
    } catch (error) {
      errorToastr({
        error,
        messagePrefix: 'Tracing Notes Update Failed',
      });
      setSubmitting(false);
    }
  },
})(TracingModal);
